import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (attempts.current >= 6) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          setTimeout(() => navigate("/dashboard"), 2200);
          return;
        }
        if (data.status === "expired") { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5">
      <div className="bg-white rounded-[2rem] border border-taupe p-10 sm:p-14 text-center max-w-md w-full shadow-[0_20px_40px_rgba(58,18,48,0.08)]">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-wine mx-auto" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-3xl text-wine">Confirming your membership…</h1>
            <p className="mt-2 text-[#9B7E8D]">One moment while we finalize your payment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-[#4A6B53] mx-auto" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-4xl text-wine">Welcome, love!</h1>
            <p className="mt-2 text-[#6E5862]">Your membership is active. Taking you to your dashboard…</p>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="h-14 w-14 text-[#9B4444] mx-auto" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-3xl text-wine">Hmm, something's off</h1>
            <p className="mt-2 text-[#6E5862]">{status === "timeout" ? "Payment is still processing. Check your dashboard shortly." : "We couldn't confirm your payment."}</p>
            <button data-testid="payment-back-btn" onClick={() => navigate("/membership")} className="mt-6 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
              Back to membership
            </button>
          </>
        )}
      </div>
    </div>
  );
}
